import React, { useEffect, useMemo, useState } from 'react';
import Icon from 'components/atoms/Icon';
import { HTSClassificationData, TariffDeterminationData, TariffAddon, TariffFlag } from 'types/chat.types';
import FeedbackButtons from 'components/molecules/chat/FeedbackButtons';
import useChatStore from 'stores/chatStore';
import { exportClassificationPdf } from 'utils/exportClassificationPdf';

/**
 * Single source of truth for how an HTS code is typeset, so every alternative
 * code renders identically. Hierarchy comes from weight and placement, not a
 * separate typeface (no monospace) — matching the prior component's decision.
 */
const HTS_CODE_CLASS = 'text-[14.5px] font-bold text-gray-900 tabular-nums';

/** Tabs available on the result card, in display order. */
type TabKey = 'alts' | 'why' | 'assumptions' | 'prog' | 'flags' | 'sources';

/** Render order + labels for the additional-program addons (tariff tab). */
const PROGRAM_ORDER = ['section_301', 'section_232', 'ieepa', 'section_122'] as const;
const PROGRAM_LABELS: Record<string, string> = {
  section_301: 'Section 301',
  section_232: 'Section 232',
  ieepa: 'IEEPA',
  section_122: 'Section 122',
};

/** Titles for tariff flags, keyed by flag kind. */
const FLAG_TITLE: Record<string, string> = {
  addcvd: 'ADD/CVD — potential scope',
  trq: 'Tariff-rate quota',
  value_required: 'Customs value required',
  staleness: 'Reference data freshness',
  scope: 'Scope note',
};

/** Map a country code to a display name, falling back to the raw code. */
const getCountryName = (code?: string): string => {
  if (!code) return '-';
  const countryMap: Record<string, string> = {
    ID: 'Indonesia',
    CN: 'China',
    US: 'United States',
    IN: 'India',
    JP: 'Japan',
    KR: 'South Korea',
    VN: 'Vietnam',
    TH: 'Thailand',
    MY: 'Malaysia',
    PH: 'Philippines',
    SG: 'Singapore',
  };
  return countryMap[code.toUpperCase()] || code;
};

/** Format a confidence score (0–1) as a rounded percentage range, e.g. "85-90%". */
const getConfidenceRange = (score?: number): string | null => {
  if (!score) return null;
  const percentage = Math.round(score * 100);
  const lowerBound = Math.max(0, percentage - 5);
  return `${lowerBound}-${percentage}%`;
};

/** Short confidence wording shown beneath the dial, mirroring the score band. */
const getConfidenceWord = (score?: number): string => {
  const pct = Math.round((score || 0) * 100);
  if (pct >= 85) return 'High conf.';
  if (pct >= 60) return 'Med conf.';
  return 'Low conf.';
};

/** Format a nullable percentage value as a string, e.g. 10 -> "10%". */
const fmtPct = (n: number | null | undefined): string =>
  n === null || n === undefined ? '—' : `${Number(n).toFixed(Number(n) % 1 === 0 ? 0 : 2)}%`;

/** Icon colour by flag severity; the surface stays neutral in every case. */
const flagIconColor = (severity: string): string =>
  severity === 'critical' ? 'text-red-500' : severity === 'info' ? 'text-gray-400' : 'text-amber-500';

/**
 * The teal answer band's confidence dial: an animated SVG donut whose arc length
 * encodes the rounded confidence percentage. Animation is gated behind
 * `prefers-reduced-motion` so it renders its final state when motion is reduced.
 */
const ConfidenceDial: React.FC<{ score?: number; range: string | null }> = ({ score, range }) => {
  const pct = Math.round((score || 0) * 100);
  const radius = 32;
  const circumference = 2 * Math.PI * radius;
  const [animated, setAnimated] = useState(false);
  const [reduceMotion, setReduceMotion] = useState(false);

  useEffect(() => {
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    setReduceMotion(reduce);
    if (reduce) {
      setAnimated(true);
      return;
    }
    const id = requestAnimationFrame(() => setAnimated(true));
    return () => cancelAnimationFrame(id);
  }, []);

  const offset = animated ? circumference * (1 - pct / 100) : circumference;

  return (
    <div className="flex flex-col items-center gap-[7px] flex-shrink-0" title={range ? `Confidence range ${range}` : undefined}>
      <svg viewBox="0 0 80 80" className="w-[78px] h-[78px] -rotate-90">
        <circle cx="40" cy="40" r={radius} fill="none" strokeWidth="7" className="stroke-white/20" />
        <circle
          cx="40"
          cy="40"
          r={radius}
          fill="none"
          strokeWidth="7"
          strokeLinecap="round"
          className="stroke-white"
          style={{
            strokeDasharray: circumference,
            strokeDashoffset: offset,
            transition: reduceMotion ? undefined : 'stroke-dashoffset 1s ease',
          }}
        />
        <text
          x="40"
          y="40"
          textAnchor="middle"
          dominantBaseline="central"
          transform="rotate(90 40 40)"
          className="fill-white text-[15px] font-extrabold"
        >
          {pct}%
        </text>
      </svg>
      <span className="text-[10.5px] font-bold uppercase tracking-[0.06em] text-[#ccfbf4]/90">
        {getConfidenceWord(score)}
      </span>
    </div>
  );
};

/**
 * A clean bulleted list item with a consistently aligned dot marker, replacing
 * raw "•" glyphs for predictable baseline alignment across panes.
 */
const BulletItem: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <li className="flex gap-2.5">
    <span className="mt-[7px] w-[5px] h-[5px] rounded-full bg-gray-300 flex-shrink-0" />
    <span>{children}</span>
  </li>
);

/**
 * Status presentation for a single tariff program. Strict two-colour budget:
 * teal marks the one applicable program, gray covers everything else (no amber
 * in this pane — caution colour lives only in the Flags tab).
 */
const tariffStatus = (addon: TariffAddon): { word: string; applicable: boolean; textClass: string } => {
  if (addon.applicable === true) return { word: 'Applicable', applicable: true, textClass: 'text-teal-700 font-semibold' };
  if (addon.excluded) return { word: 'Excluded', applicable: false, textClass: 'text-gray-400 font-medium' };
  if (addon.applicable === false) return { word: 'Not applicable', applicable: false, textClass: 'text-gray-400 font-medium' };
  return { word: 'Uncertain', applicable: false, textClass: 'text-gray-400 font-medium' };
};

/** One tariff-program row inside the Tariff programs pane. */
const TariffRow: React.FC<{ label: string; addon: TariffAddon }> = ({ label, addon }) => {
  const status = tariffStatus(addon);
  const detail = addon.basis || addon.reason;

  return (
    <div className="py-[15px] first:pt-0.5 border-t border-gray-100 first:border-t-0">
      <div className="flex items-center gap-[11px]">
        <span
          className={`w-2 h-2 rounded-full flex-shrink-0 ${
            status.applicable ? 'bg-teal-500' : 'bg-white border-[1.5px] border-gray-300'
          }`}
        />
        <span className="text-sm font-semibold text-gray-900">{label}</span>
        <span className={`text-xs ${status.textClass}`}>{status.word}</span>
        <span
          className={`ml-auto text-[13.5px] tabular-nums ${
            status.applicable ? 'font-bold text-gray-900' : 'font-semibold text-gray-300'
          }`}
        >
          {status.applicable ? addon.rate_display || fmtPct(addon.rate_pct) : '—'}
        </span>
      </div>
      {detail && <div className="ml-[19px] mt-1.5 text-[12.5px] text-gray-500 leading-[1.55]">{detail}</div>}
    </div>
  );
};

/** A single flag/warning row; severity reads only through the icon colour. */
const FlagRow: React.FC<{ title: string; message: string; severity: string; caseNumbers?: string[] }> = ({
  title,
  message,
  severity,
  caseNumbers,
}) => (
  <div className="flex gap-[11px] px-[13px] py-[11px] bg-gray-50 rounded-[10px] [&+&]:mt-2 leading-[1.55]">
    <Icon name="exclamation-triangle" className={`w-4 h-4 flex-shrink-0 mt-px ${flagIconColor(severity)}`} />
    <div className="text-[12.5px]">
      <span className="font-semibold text-gray-900">{title}: </span>
      <span className="text-gray-600">{message}</span>
      {caseNumbers && caseNumbers.length > 0 && (
        <span className="block mt-1 text-[11px] text-gray-400 tabular-nums">Case(s): {caseNumbers.join(', ')}</span>
      )}
    </div>
  </div>
);

interface HTSClassificationResultProps {
  data: HTSClassificationData;
  filename?: string;
  classificationId?: string;
  documentId?: string;
  countryOfOrigin?: string;
  /** Tariff determination for this product, folded into the Tariff programs tab. */
  tariffData?: TariffDeterminationData;
  /** Show an inline loading state while the tariff determination is computed. */
  tariffLoading?: boolean;
}

/**
 * Completed-classification output rendered inside the assistant chat bubble.
 * "Instrument" layout: a teal answer band leads with the recommended code,
 * a confidence dial and the duty summary; defensibility detail (alternatives,
 * rationale, tariff programs, flags, sources) is organised into tabs so the
 * card never sprawls.
 */
const HTSClassificationResult: React.FC<HTSClassificationResultProps> = ({
  data,
  filename,
  classificationId,
  documentId,
  countryOfOrigin,
  tariffData,
  tariffLoading,
}) => {
  const submitFeedback = useChatStore((state) => state.submitFeedback);
  const initiateReclassification = useChatStore((state) => state.initiateReclassification);
  const feedbackMap = useChatStore((state) => state.feedbackMap);

  const [copied, setCopied] = useState(false);

  const productName = filename
    ? filename.replace(/\.[^/.]+$/, '').replace(/_/g, ' ')
    : 'Product Classification';

  const confidenceRange = getConfidenceRange(data.confidence_score);
  const displayCountryOfOrigin = getCountryName(countryOfOrigin || data.country_of_origin);

  // ── Derived collections used by the tabs ──────────────────────────────────
  const alternatives = (data.additional_classifications || []).slice(0, 5);

  const whyBullets = useMemo(() => {
    const items: string[] = [];
    if (data.hts_description) items.push(`Classified under "${data.hts_description}".`);
    if (data.chapter_description) items.push(`Chapter ${data.chapter}: ${data.chapter_description}.`);
    (data.notes || []).forEach((note) => items.push(note));
    return items;
  }, [data.hts_description, data.chapter, data.chapter_description, data.notes]);

  const assumptions = data.assumptions || [];
  const sources = data.source_references || [];

  const presentPrograms = useMemo(
    () => PROGRAM_ORDER.filter((key) => tariffData?.addons?.[key]),
    [tariffData],
  );

  const flags: TariffFlag[] = tariffData?.flags || [];
  const warnings: string[] = tariffData?.warnings || [];
  const flagCount = flags.length + warnings.length;

  // Split sources into "Tariff schedule" and "CROSS rulings".
  const { schedule, crossRulings } = useMemo(() => {
    const isCross = (label: string) => label.trim().toUpperCase().startsWith('CROSS');
    return {
      schedule: sources.filter((s) => !isCross(s.label)),
      crossRulings: sources.filter((s) => isCross(s.label)),
    };
  }, [sources]);

  // ── Tab availability (omit empty tabs) ────────────────────────────────────
  const availableTabs = useMemo(() => {
    const tabs: Array<{ key: TabKey; label: string; count?: number }> = [];
    if (alternatives.length > 0) tabs.push({ key: 'alts', label: 'Alternatives', count: alternatives.length });
    if (whyBullets.length > 0) tabs.push({ key: 'why', label: 'Why this code' });
    if (assumptions.length > 0) tabs.push({ key: 'assumptions', label: 'Assumptions', count: assumptions.length });
    if (presentPrograms.length > 0) tabs.push({ key: 'prog', label: 'Tariff programs', count: presentPrograms.length });
    if (flagCount > 0) tabs.push({ key: 'flags', label: 'Flags', count: flagCount });
    if (sources.length > 0) tabs.push({ key: 'sources', label: 'Sources', count: sources.length });
    return tabs;
  }, [alternatives.length, whyBullets.length, assumptions.length, presentPrograms.length, flagCount, sources.length]);

  const [activeTab, setActiveTab] = useState<TabKey>('alts');

  // Default to the first available tab if the preferred one isn't present.
  useEffect(() => {
    if (availableTabs.length === 0) return;
    if (!availableTabs.some((t) => t.key === activeTab)) {
      setActiveTab(availableTabs[0].key);
    }
  }, [availableTabs, activeTab]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleCopy = () => {
    const text = `HTS Code: ${data.primary_hts_code}\nDescription: ${data.hts_description || ''}\nCategory: ${data.product_category || ''}`;
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1400);
  };

  const handleExportPdf = () => {
    exportClassificationPdf(
      data,
      { productName, countryOfOrigin: displayCountryOfOrigin, classificationId },
      tariffData,
    );
  };

  const handleFeedback = async (id: string, isPositive: boolean) => {
    await submitFeedback(id, isPositive);
  };

  const handleThumbsDown = () => {
    if (!documentId) {
      console.error('No document ID available for reclassification');
      return;
    }
    initiateReclassification(documentId, classificationId || '');
  };

  const currentFeedback = classificationId ? feedbackMap.get(classificationId) || null : null;

  // ── Answer-band meta strip cells ──────────────────────────────────────────
  const baseDuty =
    tariffData?.base_mfn_rate?.rate_display ||
    data.hts_duty_rate ||
    data.duty_rate ||
    data.base_duty ||
    '-';

  return (
    <div className="bg-white border border-gray-200 rounded-[18px] overflow-hidden shadow-[0_1px_2px_rgba(16,24,40,.04),0_1px_3px_rgba(16,24,40,.04)]">
      {/* ===== answer band ===== */}
      <div
        className="px-[26px] py-6 text-white"
        style={{ background: 'linear-gradient(160deg, #0a5f54 0%, #063b34 100%)' }}
      >
        <div className="flex items-start justify-between gap-3.5">
          <span className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-[#ccfbf4]/80">
            Recommended Classification
          </span>
          <div className="flex gap-1">
            <button
              onClick={handleCopy}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-white/90 hover:text-white hover:bg-white/[0.14] rounded-lg transition-colors"
            >
              <Icon name={copied ? 'check' : 'copy'} className="w-3.5 h-3.5" />
              {copied ? 'Copied' : 'Copy'}
            </button>
            <button
              onClick={handleExportPdf}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-white/90 hover:text-white hover:bg-white/[0.14] rounded-lg transition-colors"
              title="Export this result as a PDF"
            >
              <Icon name="download" className="w-3.5 h-3.5" />
              Export PDF
            </button>
          </div>
        </div>

        <div className="flex items-end justify-between gap-[18px] mt-2.5">
          <div className="min-w-0">
            <div className="text-[34px] font-bold tracking-[-0.02em] leading-none tabular-nums">
              {data.primary_hts_code}
            </div>
            {(data.hts_description || data.product_category) && (
              <div className="text-[12.5px] leading-[1.5] text-white/[0.78] mt-[11px] max-w-[420px]">
                {data.hts_description || data.product_category}
              </div>
            )}
          </div>
          {confidenceRange && <ConfidenceDial score={data.confidence_score} range={confidenceRange} />}
        </div>

        <div className="flex flex-wrap gap-x-[22px] gap-y-3 mt-[18px] pt-4 border-t border-white/[0.14]">
          <div>
            <div className="text-[10.5px] text-[#ccfbf4]/70 mb-1 whitespace-nowrap">Origin</div>
            <div className="text-[15px] font-bold tabular-nums">{displayCountryOfOrigin}</div>
          </div>
          <div>
            <div className="text-[10.5px] text-[#ccfbf4]/70 mb-1 whitespace-nowrap">Base duty</div>
            <div className="text-[15px] font-bold tabular-nums">{baseDuty}</div>
          </div>
          {tariffData && (
            <>
              <div>
                <div className="text-[10.5px] text-[#ccfbf4]/70 mb-1 whitespace-nowrap">Additional</div>
                <div className="text-[15px] font-bold tabular-nums">+{fmtPct(tariffData.total_additional_pct)}</div>
              </div>
              <div>
                <div className="text-[10.5px] text-[#ccfbf4]/70 mb-1 whitespace-nowrap">Total rate</div>
                <div className="text-[15px] font-bold tabular-nums text-[#7fe9da]">
                  {fmtPct(tariffData.estimated_total_duty_pct)}
                  {tariffData.has_specific_components && (
                    <span className="text-[11px] font-medium text-white/70"> + per-unit</span>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* inline tariff loading state */}
      {tariffLoading && !tariffData && (
        <div className="flex items-center gap-2.5 px-[26px] py-3 border-b border-gray-200">
          <div className="w-4 h-4 border-2 border-teal-500 border-t-transparent rounded-full animate-spin flex-shrink-0" />
          <span className="text-sm text-gray-500">Looking up duty rates…</span>
        </div>
      )}

      {/* ===== tabs ===== */}
      {availableTabs.length > 0 && (
        <div className="flex gap-0.5 px-[18px] border-b border-gray-200 overflow-x-auto">
          {availableTabs.map((tab) => {
            const isActive = tab.key === activeTab;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-[7px] px-3.5 py-[15px] text-[13px] font-semibold whitespace-nowrap border-b-2 transition-colors ${
                  isActive
                    ? 'text-teal-700 border-teal-600'
                    : 'text-gray-500 border-transparent hover:text-gray-800'
                }`}
              >
                {tab.label}
                {typeof tab.count === 'number' && (
                  <span
                    className={`text-[10.5px] font-bold rounded-full px-[7px] py-px leading-none ${
                      isActive ? 'bg-teal-50 text-teal-700' : 'bg-gray-100 text-gray-500'
                    }`}
                  >
                    {tab.count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* ===== panes ===== */}
      {availableTabs.length > 0 && (
        <div className="px-[26px] py-6">
          {/* Alternatives */}
          {activeTab === 'alts' && (
            <div>
              {alternatives.map((alt, index) => {
                const pct = Math.round((alt.confidence || 0) * 100);
                return (
                  <div
                    key={index}
                    className="border border-gray-200 rounded-xl px-[15px] py-[13px] [&+&]:mt-2.5 hover:border-gray-300 transition-colors"
                  >
                    <div className="flex items-center gap-3 mb-1.5">
                      <span className={HTS_CODE_CLASS}>{alt.hts_code}</span>
                      {index === 0 && (
                        <span className="text-[10.5px] font-bold uppercase tracking-[0.04em] text-gray-400">
                          Next best
                        </span>
                      )}
                      <span className="ml-auto flex items-center gap-2.5">
                        <span className="w-[62px] h-1.5 rounded-full bg-gray-200 overflow-hidden">
                          <span className="block h-full rounded-full bg-gray-400" style={{ width: `${pct}%` }} />
                        </span>
                        <span className="text-xs font-semibold text-gray-500 w-[34px] text-right tabular-nums">
                          {pct}%
                        </span>
                      </span>
                    </div>
                    {alt.description && (
                      <div className="text-[12.5px] text-gray-600 leading-[1.5]">{alt.description}</div>
                    )}
                    {alt.reason_rejected && (
                      <div className="text-[12.5px] text-gray-500 leading-[1.5] mt-1.5">
                        <span className="font-semibold text-gray-700">Why not: </span>
                        {alt.reason_rejected}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Why this code */}
          {activeTab === 'why' && (
            <ul className="space-y-[9px] text-[13px] text-gray-600 leading-[1.55]">
              {whyBullets.map((bullet, index) => (
                <BulletItem key={index}>{bullet}</BulletItem>
              ))}
            </ul>
          )}

          {/* Assumptions */}
          {activeTab === 'assumptions' && (
            <ul className="space-y-[9px] text-[13px] text-gray-600 leading-[1.55]">
              {assumptions.map((assumption, index) => (
                <BulletItem key={index}>{assumption}</BulletItem>
              ))}
            </ul>
          )}

          {/* Tariff programs — strict two-colour */}
          {activeTab === 'prog' && tariffData && (
            <div>
              {tariffData.stacking_order && tariffData.stacking_order.length > 1 && (
                <div className="text-xs text-gray-500 mb-3.5 leading-[1.5]">
                  <span className="font-semibold text-gray-700">Stacking: </span>
                  {tariffData.stacking_order.join(' → ')} (additive on customs value)
                </div>
              )}
              <div className="flex flex-col">
                {presentPrograms.map((key) => (
                  <TariffRow key={key} label={PROGRAM_LABELS[key]} addon={tariffData.addons[key]} />
                ))}
              </div>
            </div>
          )}

          {/* Flags — the only place caution colour appears */}
          {activeTab === 'flags' && (
            <div>
              {flags.map((flag, index) => (
                <FlagRow
                  key={`flag-${index}`}
                  title={FLAG_TITLE[flag.kind] || 'Note'}
                  message={flag.message}
                  severity={flag.severity}
                  caseNumbers={
                    flag.kind === 'addcvd' && Array.isArray(flag.detail?.case_numbers)
                      ? (flag.detail!.case_numbers as string[])
                      : undefined
                  }
                />
              ))}
              {warnings.map((warning, index) => (
                <FlagRow key={`warn-${index}`} title="Note" message={warning} severity="warning" />
              ))}
            </div>
          )}

          {/* Sources */}
          {activeTab === 'sources' && (
            <div>
              <div className="text-[12.5px] text-gray-500 leading-[1.55] mb-[18px]">
                {sources.length} reference{sources.length !== 1 ? 's' : ''} support this classification.
                {crossRulings.length > 0 && (
                  <> CROSS rulings are CBP decisions matched on product, material and country of origin.</>
                )}
              </div>
              {schedule.length > 0 && <SourceGroup heading="Tariff schedule" sources={schedule} />}
              {crossRulings.length > 0 && (
                <div className={schedule.length > 0 ? 'mt-5' : ''}>
                  <SourceGroup heading="CROSS rulings" sources={crossRulings} count={crossRulings.length} />
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ===== footer ===== */}
      <div className="flex items-end justify-between gap-[18px] px-[26px] pb-5 pt-1">
        <div className="text-xs text-gray-500">
          {sources.length > 0 ? (
            <>
              {sources.length} reference{sources.length !== 1 ? 's' : ''} ·{' '}
              <button
                type="button"
                onClick={() => setActiveTab('sources')}
                className="text-teal-700 font-semibold hover:underline"
              >
                View all sources →
              </button>
            </>
          ) : (
            <span className="text-gray-400">No external references cited.</span>
          )}
        </div>
        {(classificationId || documentId) && (
          <FeedbackButtons
            classificationId={classificationId || ''}
            onFeedback={handleFeedback}
            onThumbsDown={documentId ? handleThumbsDown : undefined}
            initialFeedback={currentFeedback}
          />
        )}
      </div>
    </div>
  );
};

/**
 * Parse a source label into a prominent ID line and an optional title. Labels
 * like "CROSS Ruling N233993: The tariff classification…" split on the first
 * colon; labels without a colon render as a single bold ID line.
 */
const parseSourceLabel = (label: string): { id: string; title?: string } => {
  const idx = label.indexOf(':');
  if (idx > -1) {
    return { id: label.slice(0, idx).trim(), title: label.slice(idx + 1).trim() };
  }
  return { id: label.trim() };
};

/**
 * A titled group of source links (e.g. "Tariff schedule" / "CROSS rulings"),
 * built to scale to dozens of references. Each row is an external link whose
 * trailing arrow fades in on hover.
 */
const SourceGroup: React.FC<{
  heading: string;
  sources: Array<{ label: string; url?: string | null }>;
  count?: number;
}> = ({ heading, sources, count }) => (
  <div>
    <div className="flex items-center gap-2.5 text-[11px] font-bold uppercase tracking-[0.07em] text-gray-400 mb-1">
      {heading}
      {typeof count === 'number' && (
        <span className="text-[11px] font-bold normal-case tracking-normal text-gray-400 bg-gray-100 rounded-full px-2 py-px">
          {count}
        </span>
      )}
    </div>
    {sources.map((source, index) => {
      const { id, title } = parseSourceLabel(source.label);
      const rowClass =
        'group flex gap-3 items-start py-[11px] border-t border-gray-100 first:border-t-0 no-underline';
      const inner = (
        <>
          <span className="w-[30px] h-[30px] rounded-lg bg-gray-50 border border-gray-100 flex items-center justify-center flex-shrink-0">
            <Icon name="document" className="w-[15px] h-[15px] text-gray-400" />
          </span>
          <span className="min-w-0 flex-1 flex flex-col">
            <span className="text-[13px] font-bold text-gray-900 group-hover:text-teal-700 transition-colors">
              {id}
            </span>
            {title && <span className="text-[13px] text-gray-500 leading-[1.5] mt-0.5">{title}</span>}
          </span>
          {source.url && (
            <span className="flex-shrink-0 self-center opacity-0 group-hover:opacity-100 transition-opacity">
              <Icon name="external-link" className="w-[15px] h-[15px] text-gray-400" />
            </span>
          )}
        </>
      );
      return source.url ? (
        <a key={index} href={source.url} target="_blank" rel="noopener noreferrer" className={rowClass}>
          {inner}
        </a>
      ) : (
        <div key={index} className={rowClass}>
          {inner}
        </div>
      );
    })}
  </div>
);

export default HTSClassificationResult;
